import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Radar, RefreshCw, Ship } from "lucide-react";
import { toast } from "sonner";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { formatDate, formatIsoDay } from "@/lib/format";
import { sel } from "@/lib/finance";

const daysLeft = (iso) => (iso ? Math.ceil((new Date(`${iso}T00:00:00`) - new Date(new Date().toDateString())) / 86400000) : null);

function RouteMap({ id, onClose }) {
  const ref = useRef(null);
  const [label, setLabel] = useState("");
  useEffect(() => {
    let map, gone = false;
    api.get(`/imports/${id}/tracking/route`).then(({ data }) => {
      if (gone || !ref.current || ref.current._leaflet_id) return;
      const mp = data.map_point;
      map = L.map(ref.current, { worldCopyJump: true }).setView([mp?.lat || 30, mp?.lng || 30], 3);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18, attribution: "© OpenStreetMap" }).addTo(map);
      if (data.geojson) {
        const layer = L.geoJSON(data.geojson, { style: { color: "#2563eb", weight: 3, opacity: 0.8 },
          pointToLayer: (f, ll) => L.circleMarker(ll, { radius: 5, color: f.properties?.status === "FUTURE" ? "#94a3b8" : "#2563eb" }).bindTooltip(f.properties?.location?.name || "") }).addTo(map);
        try { map.fitBounds(layer.getBounds(), { padding: [20, 20] }); } catch { /* boş geojson */ }
      }
      if (mp) L.circleMarker([mp.lat, mp.lng], { radius: 10, color: "#dc2626", fillColor: "#dc2626", fillOpacity: 0.6 }).addTo(map).bindPopup(mp.label).openPopup();
      setLabel(mp?.label || "Konum bilgisi yok");
    });
    return () => { gone = true; map?.remove(); };
  }, [id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="tracking-map-dialog">
        <DialogHeader><DialogTitle>Konteyner Rotası</DialogTitle></DialogHeader>
        <div className="text-[12px] text-slate-600" data-testid="tracking-map-label">{label}</div>
        <div ref={ref} style={{ height: 480 }} className="rounded border border-slate-200" data-testid="tracking-map" />
      </DialogContent>
    </Dialog>
  );
}

function StartDialog({ imp, onClose, onDone }) {
  const [kind, setKind] = useState(imp.mbl_no ? "mbl" : imp.container_no ? "container" : "booking");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const def = { mbl: imp.mbl_no, container: imp.container_no, booking: imp.booking_no }[kind] || "";
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${imp.id}/tracking`, { kind, value: value || def, confirm: true });
      toast.success({ olusturuldu: "ShipsGo takibi oluşturuldu (1 kredi)", shipsgo_mevcut_baglandi: "ShipsGo'daki mevcut kayda bağlandı (kredi harcanmadı)", erp_yeniden_kullanildi: "Mevcut takip yeniden kullanıldı", mevcut: "Takip zaten aktif" }[data.result] || "Takip başladı");
      if (data.warning) toast.warning(data.warning);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tracking-start-dialog">
        <DialogHeader><DialogTitle>Canlı Takip Başlat</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          <select className={sel} value={kind} onChange={(e) => setKind(e.target.value)} data-testid="tracking-kind">
            <option value="mbl">MBL (önerilen)</option><option value="container">Konteyner No</option><option value="booking">Booking No</option></select>
          <Input className="col-span-2" placeholder={def || "Numara"} value={value} onChange={(e) => setValue(e.target.value.toUpperCase())} data-testid="tracking-value" />
        </div>
        <div className="text-[12px] bg-amber-50 border border-amber-300 text-amber-900 rounded p-2" data-testid="tracking-credit-warning">
          <b>Bu işlem ShipsGo kredisi kullanır.</b> Önce ERP'de ve ShipsGo'da aynı numara aranır; varsa kredi harcanmadan bağlanır.
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button disabled={busy || !(value || def)} onClick={go} className="bg-blue-600 hover:bg-blue-700" data-testid="tracking-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Onaylıyorum, Başlat</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TrackingCard({ imp, canEdit, onChange }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get(`/imports/${imp.id}/tracking`).then(({ data }) => setT(data));
  useEffect(() => { load(); }, [imp.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = async () => {
    setBusy(true);
    try { await api.post(`/imports/${imp.id}/tracking/refresh`); toast.success("Takip güncellendi"); await load(); onChange(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  if (!t) return <div className="panel p-3"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  const lg = t.logistics, left = daysLeft(lg.current_eta);
  return (
    <div className="panel p-4" data-testid="tracking-card">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Radar className="h-5 w-5 text-blue-600" /><span className="font-semibold">Canlı Konteyner Takibi</span>
        {t.tracking && <span className="text-[11px] px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-800" data-testid="tracking-status">{lg.logistics_status_label || "Bilinmiyor"}{lg.logistics_status ? ` · ${lg.logistics_status}` : ""}</span>}
        <div className="ml-auto flex gap-2">
          {t.tracking ? <>
            <Button size="sm" variant="outline" disabled={busy} onClick={refresh} data-testid="tracking-refresh">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}Şimdi Güncelle</Button>
            {(t.has_route || t.map_point) && <Button size="sm" variant="outline" onClick={() => setDlg("map")} data-testid="tracking-map-button"><MapPin className="h-4 w-4 mr-1" />Haritada Göster</Button>}
          </> : canEdit && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={!t.key_present} title={t.key_present ? "" : "SHIPSGO_API_KEY tanımlı değil"} onClick={() => setDlg("start")} data-testid="tracking-start"><Ship className="h-4 w-4 mr-1" />Canlı Takip Başlat</Button>}
        </div>
      </div>
      {!t.tracking ? <p className="text-[12px] text-slate-500" data-testid="tracking-empty">{t.key_present ? "Bu dosyada ShipsGo takibi yok." : "ShipsGo API anahtarı tanımlı değil (Ayarlar → Entegrasyonlar)."}</p> : (<>
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div className="border border-slate-200 rounded p-2"><div className="text-[11px] text-slate-500">Tahmini Varış</div><div className="text-lg font-bold font-mono" data-testid="tracking-eta">{formatIsoDay(lg.current_eta)}</div>
            <div className="text-[12px]">{left != null && <span data-testid="tracking-days-left">{left > 0 ? `Kalan: ${left} Gün` : left === 0 ? "Bugün varış" : `Vardı: ${formatIsoDay(lg.current_eta)} (${-left} gün önce)`}</span>}
              {lg.delay_days != null && lg.delay_days !== 0 && <span className={`ml-2 ${lg.delay_days > 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="tracking-delay">{lg.delay_days > 0 ? `+${lg.delay_days} gün gecikme` : `${-lg.delay_days} gün erken`}</span>}</div>
            <div className="text-[11px] text-slate-400">İlk ETA {formatIsoDay(lg.first_eta)}</div></div>
          <div className="border border-slate-200 rounded p-2 text-[12px] space-y-0.5"><div>Hat: <b>{lg.shipping_line || "—"}</b></div><div>Gemi / Sefer: <b>{lg.vessel || "—"}</b> {lg.voyage}</div>
            <div>{lg.pol_name || "—"} → {lg.pod_name || "—"}</div><div>ETD {formatIsoDay(lg.etd)} · Transit {lg.transit_time_days ?? "—"} gün</div></div>
          <div className="border border-slate-200 rounded p-2 text-[12px] space-y-0.5"><div>Aktarma: <b>{lg.transshipments?.length ? lg.transshipments.join(", ") : "—"}</b></div><div>CO₂: {lg.co2_kg ?? "—"} kg</div>
            <div data-testid="tracking-position">Konum: {t.map_point ? t.map_point.label.replace("Son konum: ", "") : "—"}</div><div className="text-slate-400">Son güncelleme {lg.last_synced_at ? formatDate(lg.last_synced_at) : "—"}</div></div>
        </div>
        <div className="grid md:grid-cols-2 gap-3 text-[12px]">
          <div data-testid="tracking-milestones"><div className="font-semibold text-slate-600 mb-1">Olaylar</div>
            {(lg.milestones || []).slice(-8).reverse().map((m, i) => <div key={i} className={m.status === "EST" ? "text-slate-400" : ""}>{m.timestamp?.slice(0, 10)} · {m.event} · {m.location}{m.vessel ? ` · ${m.vessel}` : ""}{m.status === "EST" ? " (tahmini)" : ""}</div>)}</div>
          <div data-testid="tracking-eta-history"><div className="font-semibold text-slate-600 mb-1">ETA Değişimleri</div>
            {t.eta_history.length === 0 ? <div className="text-slate-400">—</div> : t.eta_history.slice().reverse().map((h, i) => <div key={i}>{formatDate(h.at)} · {formatIsoDay(h.old)} → <b>{formatIsoDay(h.new)}</b> ({h.source})</div>)}</div>
        </div>
      </>)}
      {dlg === "start" && <StartDialog imp={imp} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); onChange(); }} />}
      {dlg === "map" && <RouteMap id={imp.id} onClose={() => setDlg(null)} />}
    </div>
  );
}
